"use client";

import { type ChangeEvent } from "react";
import { usePathname, useRouter } from "next/navigation";
import GridColumn from "../common/grid-column/grid-column";
import GridRow from "../common/grid-row/grid-row";
import SuperTitle from "../common/supertitle/supertitle";
import { UserFeedback } from "@/sanity/interfaces";
import Link, { LinkStyle, LinkType } from "@/components/common/link/link";
//@todo we might need to create common review compoanent
import HomeReviews from "../home/home-reviews/home-reviews";
import { ReviewPage } from "@/sanity/sanity.types";

interface IReviewsProps {
  data: ReviewPage;
  reviews: UserFeedback[];
  averageRating: number;
  totalReviews: number;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: string;
}

const Reviews = (props: IReviewsProps) => {
  const {
    data,
    reviews,
    averageRating,
    totalReviews,
    page,
    pageSize,
    sortBy,
    sortDir,
  } = props;
  const { results } = data;
  const router = useRouter();
  const pathname = usePathname();
  const totalPages = Math.ceil(totalReviews / pageSize);

  const setReviewsParams = (newParams: {
    sortBy?: string;
    sortDir?: string;
    page?: number;
  }) => {
    const params = new URLSearchParams();

    params.set("sortBy", newParams.sortBy ?? sortBy);
    params.set("sortDir", newParams.sortDir ?? sortDir);
    params.set("page", String(newParams.page ?? page));
    router.push(`${pathname}?${params.toString()}`);
  };

  const onSortSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const [newSortBy, newSortDir] = e.target.value.split("_");

    setReviewsParams({
      sortBy: newSortBy,
      sortDir: newSortDir,
      page: 1,
    });
  };

  const getPaginationRange = () => {
    const delta = 2; // how many pages to show around current
    const range: (number | string)[] = [];
    const rangeWithDots: (number | string)[] = [];
    let lastPage: number | null = null;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= page - delta && i <= page + delta)
      ) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (lastPage !== null) {
        if ((i as number) - lastPage === 2) {
          rangeWithDots.push(lastPage + 1);
        } else if ((i as number) - lastPage > 2) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      lastPage = i as number;
    }

    return rangeWithDots;
  };

  return (
    <>
      <GridRow className="relative min-h-screen">
        {/* Gradient overlay */}
        <div className="absolute left-0 top-0 h-[430px] w-full bg-gradient-to-b from-[#FAFBFB00] to-[#EAF3FF] md:h-[440px] lg:h-[450px]" />
        <GridColumn className="mt-9 flex flex-col">
          {/* Summary Section */}
          <div className="relative z-10 text-center">
            <div className="mb-5 flex justify-center text-[22px] text-[#219653]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="#219653"
                viewBox="0 0 24 24"
                width="48px"
                height="48px"
                aria-hidden="true"
              >
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            </div>
            <div className="flex flex-col items-center">
              <div className="my-2 font-[lora] text-[78px] font-bold leading-[25px]">
                {averageRating}
              </div>
              <span className="mt-9 rounded-[20px] bg-[#333] px-[22px] py-2 text-[24px] font-semibold text-white">
                {totalReviews.toLocaleString()} Reviews
              </span>
              <h1 className="mt-5 font-[lora] text-[28px] font-bold md:text-[48px]">
                Client Reviews
              </h1>
              <span className="mx-auto my-2 max-w-[800px] text-center font-work-sans text-[20px] leading-7 md:text-[22px]">
                Our clients&apos; feedback reflects our commitment to helping
                you save money and reach your financial goals.
              </span>
            </div>
          </div>

          {/* Reviews List */}
          <div>
            <div className="mb-4 mt-[40px] flex flex-col items-center gap-2 md:mt-[80px] md:flex-row md:justify-between">
              <h2 className="font-[lora] text-[28px] font-bold md:text-[32px]">
                Recent Reviews
              </h2>
              <label className="flex items-center">
                <span className="mr-1.5 font-[lora] text-[18px]">
                  Sort by :
                </span>
                <select
                  value={`${sortBy}_${sortDir}`}
                  onChange={onSortSelect}
                  className="cursor-pointer border-none bg-transparent text-[16px] font-semibold text-[#00698f]"
                >
                  <option value="nServiceAnswer_DESC">Highest Rating</option>
                  <option value="nServiceAnswer_ASC">Lowest Rating</option>
                  <option value="dEntered_DESC">Newest</option>
                  <option value="dEntered_ASC">Oldest</option>
                </select>
              </label>
            </div>

            <div>
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="mb-5 rounded-[6px] border-2 border-[#e2e2e2] bg-white px-5 py-3 shadow-[20px_8px_80px_0_rgba(0,0,0,0.04)]"
                >
                  <div className="flex justify-start py-[5px] text-[22px] text-[#219653]">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <svg
                        key={i}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="#219653"
                        viewBox="0 0 24 24"
                        width="24px"
                        height="24px"
                        aria-hidden="true"
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    ))}
                  </div>
                  <p className="my-3 text-[16px] leading-[1.5] text-[#1d2b36] md:text-[18px]">
                    {review.comment}
                  </p>
                  <div className="mt-3 text-sm text-gray-500">
                    <span className="mr-2">{review.name}</span>{" "}
                    <span className="mr-2"> | </span>
                    <span>{review.date}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}

            <div className="mx-6 my-10 flex items-center justify-center md:my-20">
              {/* Desktop Pagination */}
              <div className="hidden items-center gap-2 md:flex">
                {/* Previous */}
                <button
                  onClick={() =>
                    setReviewsParams({ page: Math.max(1, page - 1) })
                  }
                  disabled={page === 1}
                  className="px-3 py-1 text-gray-500 disabled:opacity-50"
                >
                  Previous
                </button>

                {/* Pages */}
                {getPaginationRange().map((p, idx) =>
                  p === "..." ? (
                    <span key={idx} className="px-3 py-1">
                      ...
                    </span>
                  ) : (
                    <button
                      key={idx}
                      onClick={() => setReviewsParams({ page: p as number })}
                      className={`rounded px-3 py-1 ${
                        page === p
                          ? "bg-[#373B37] text-white"
                          : "hover:bg-gray-200"
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}

                {/* Next */}
                <button
                  onClick={() =>
                    setReviewsParams({ page: Math.min(totalPages, page + 1) })
                  }
                  disabled={page === totalPages}
                  className="px-3 py-1 text-gray-500 disabled:opacity-50"
                >
                  Next
                </button>
              </div>

              {/* Mobile Load More */}
              <div className="block w-full text-center md:hidden">
                {page < totalPages && (
                  <button
                    onClick={() => setReviewsParams({ page: page + 1 })}
                    className="mb-2 w-full cursor-pointer border border-[#00698f] bg-[#00698f] p-2 font-medium text-white"
                  >
                    Load More
                  </button>
                )}
              </div>
            </div>
          </div>
        </GridColumn>
      </GridRow>
      {/* Video Testimonials */}
      <div className="mt-[10px]">
        <div className="mb-5 flex justify-center">
          <SuperTitle>OUR RESULT</SuperTitle>
        </div>
        <div className="mb-10">
          {results && <HomeReviews data={results} showStats={false} />}
        </div>
      </div>
      <GridRow>
        <GridColumn>
          {/* CTA Banner */}
          <div className="mx-auto mb-10 w-full rounded-lg bg-[#005472] p-12 text-center text-white md:my-10">
            <span className="font-[lora] text-[28px] font-semibold md:text-[36px]">
              Ready To See Your Savings?
            </span>
            <p className="mt-2 text-[16px] md:text-[18px]">
              Get your free personalized loan options today.
            </p>
            <div className={`mt-4 grid grid-cols-1 lg:px-[35%]`}>
              <Link
                href="https://apply.amerisave.com/quote/v1/home"
                linkType={LinkType.BUTTON}
                linkStyle={LinkStyle.SECONDARY}
                className="!w-full whitespace-nowrap"
              >
                See What You Qualify For
              </Link>
            </div>
          </div>
        </GridColumn>
      </GridRow>
    </>
  );
};

export default Reviews;
